import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, Calendar, Truck, Users, Package, CheckCircle, Clock, 
  PauseCircle, XCircle, Search, Eye, RefreshCw, Loader2, Building,
  Phone, Mail, DollarSign, Route, AlertCircle, Download, BarChart3
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { useClientPermissions } from '../hooks/useClientPermissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configuration des statuts de contrat
const CONTRACT_STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700 border-gray-300', icon: Clock },
  active: { label: 'Actif', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle },
  suspended: { label: 'Suspendu', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: PauseCircle },
  closed: { label: 'Clôturé', color: 'bg-slate-100 text-slate-700 border-slate-300', icon: XCircle },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-700 border-red-300', icon: AlertCircle }
};

const TERMINOLOGY_CONFIG = {
  transfert: { label: 'Transfert', plural: 'Transferts' },
  operation: { label: 'Opération', plural: 'Opérations' },
  course: { label: 'Course', plural: 'Courses' }
};

const ContractStatusBadge = ({ status }) => {
  const config = CONTRACT_STATUS_CONFIG[status] || CONTRACT_STATUS_CONFIG.draft;
  const Icon = config.icon;
  return (
    <Badge className={cn('flex items-center gap-1.5 px-2 py-1 text-xs border', config.color)}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

const ClientContracts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const permissions = useClientPermissions();
  
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/contracts?limit=100000`);
      setContracts(response.data.items || response.data || []);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      toast.error('Erreur lors du chargement des contrats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const formatPrice = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  // Filtrage des contrats
  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = !searchTerm || 
      (contract.contractNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (contract.operationalDetails?.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || contract.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Statistiques
  const stats = {
    total: contracts.length,
    active: contracts.filter(c => c.status === 'active').length,
    suspended: contracts.filter(c => c.status === 'suspended').length,
    closed: contracts.filter(c => c.status === 'closed').length
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6" data-testid="client-contracts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg md:text-2xl font-bold text-gray-900">Mes Contrats</h1>
          <p className="text-sm text-gray-500">Consultez vos contrats de location mensuelle</p>
        </div>
        <Button variant="outline" onClick={fetchContracts} disabled={loading}>
          <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
          Actualiser
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <FileText className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Actifs</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.active}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <PauseCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Suspendus</p>
                <p className="text-2xl font-bold text-amber-600">{stats.suspended}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <XCircle className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Clôturés</p>
                <p className="text-2xl font-bold text-slate-600">{stats.closed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Contrats Overview Cards */}
      {filteredContracts.length > 0 && filteredContracts.some(c => c.runsStats) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="contracts-overview">
          {filteredContracts.filter(c => c.status === 'active').slice(0, 6).map(contract => {
            const total = contract.runsStats?.total || 0;
            const completed = contract.runsStats?.completed || 0;
            const progress = total > 0 ? Math.round((completed / total) * 100) : 0;
            return (
              <Card key={contract.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/mes-contrats/${contract.id}`)} data-testid={`overview-card-${contract.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-mono text-gray-500">{contract.contractNumber}</span>
                    <ContractStatusBadge status={contract.status} />
                  </div>
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>{completed} / {total} transferts</span>
                      <span className="font-medium">{progress}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className="bg-[#1db584] h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{new Date(contract.startDate).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })} → {new Date(contract.endDate).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}</span>
                    {permissions.canViewPrices && contract.billingRules?.monthlyForfait && (
                      <span className="font-medium">{new Intl.NumberFormat('fr-FR').format(contract.billingRules.monthlyForfait)} FCFA/mois</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Filtres */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher par numéro ou description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-full md:w-auto">
              <TabsList>
                <TabsTrigger value="all">Tous</TabsTrigger>
                <TabsTrigger value="active">Actifs</TabsTrigger>
                <TabsTrigger value="suspended">Suspendus</TabsTrigger>
                <TabsTrigger value="closed">Clôturés</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardContent>
      </Card>

      {/* Liste des contrats */}
      <Card>
        <CardContent className="p-0">
          {filteredContracts.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <h3 className="text-lg font-medium text-gray-700">Aucun contrat trouvé</h3>
              <p className="text-sm text-gray-500">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Essayez de modifier vos filtres'
                  : "Vous n'avez pas encore de contrat de location"}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>N° Contrat</TableHead>
                  <TableHead>Période</TableHead>
                  <TableHead>Type</TableHead>
                  {permissions.canViewPrices && <TableHead>Forfait mensuel</TableHead>}
                  <TableHead>Statut</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContracts.map((contract) => (
                  <TableRow 
                    key={contract.id} 
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/mes-contrats/${contract.id}`)}
                    data-testid={`contract-row-${contract.id}`}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-400" />
                        {contract.contractNumber}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p>{formatDate(contract.startDate)}</p>
                        <p className="text-xs text-gray-500">→ {formatDate(contract.endDate)}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {TERMINOLOGY_CONFIG[contract.terminology]?.plural || 'Opérations'}
                      </Badge>
                    </TableCell>
                    {permissions.canViewPrices && (
                      <TableCell className="font-medium">
                        {formatPrice(contract.billingRules?.monthlyForfait)}
                      </TableCell>
                    )}
                    <TableCell>
                      <ContractStatusBadge status={contract.status} />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/mes-contrats/${contract.id}`)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientContracts;
